/**
 * Copy and error anchoring shared by the preview and publish steps.
 *
 * Kept beside the components rather than in `config/compositeSourceEnums.js` — that file holds
 * the WORKSPACE's copy (target picker, list, empty states), and mixing wizard wording into it
 * would make one frozen object the owner of two unrelated screens. Same precedent as the
 * relations step's own `relationsCopy.js`.
 */

import { describeApiError, describeValidationFailure } from '../wizardErrors';

export const PreviewPublishCopy = Object.freeze({
  // ---- Preview ----
  PreviewRunning: 'Running this source…',
  PreviewRerun: 'Re-run',
  PreviewFailedTitle: 'This source could not be run',
  PreviewFailedFallback: 'Failed to run this source',
  PreviewEmptyTitle: 'No rows to show',
  PreviewEmptyMessage: 'The source compiles but returns no rows for this target.',
  PreviewNullCell: '—',

  // ---- Grain ----
  GrainRootNote: 'One row per record in the source data.',
  GrainFanOutTitle: 'This source multiplies rows',

  // ---- Publish ----
  PublishSummaryTitle: 'What gets created',
  PublishAction: 'Publish this source',
  PublishRunning: 'Publishing…',
  PublishFailedFallback: 'Failed to publish this source',
  PublishNoTagDesignation: 'None',
  PublishSuccessTitle: 'This source is published',
  PublishSuccessHint:
    'It now appears under Composite sources in the dashboard widget picker.',
  PublishedStateTitle: 'This source is already published',
  PublishedStateMessage:
    'Edit the local working copy, then use Apply changes. The live view stays available unless the complete replacement succeeds.',
  BrokenStateTitle: 'This source needs repair',
  BrokenStateMessage:
    'Edit the local working copy, then use Repair source. A successful repair publishes the same source and view identity.',
  PublishSaveFirstMessage: 'Save this draft before publishing it.',
  PublishPreviewFirstMessage: 'Run Preview successfully for this exact saved draft before publishing it.',

  // ---- Shared ----
  NoDefinitionTitle: 'Nothing to publish yet',
  NoDefinitionMessage:
    'Finish the earlier steps first — a source is saved as a draft before it can be published.',
});

/**
 * Which step a validator complaint belongs to, read from its `path` rather than its `code`.
 *
 * The server answers a rejected definition with `{code, path, message}` per broken rule, and the
 * paths are structural — `root…`, `edges[2].alias`, `columns[0]`, `tagColumnSource`, `grain`
 * (DataSourceDefinitionValidator.cs:143-767). Testing the path prefix therefore covers rules
 * nobody has enumerated here yet, where a code whitelist would go stale the first time the
 * validator grows one. An unrecognized path simply yields no anchor and the message stands alone.
 */
const StepAnchorByPathPrefix = Object.freeze([
  { prefix: 'root', step: 'Source step' },
  { prefix: 'edges', step: 'Relations step' },
  // Grain is not authored — it is derived from whether an edge fans out, so it is fixed where
  // the edges are.
  { prefix: 'grain', step: 'Relations step' },
  { prefix: 'columns', step: 'Columns step' },
  // The tag designation radio lives in the columns step, not in relations.
  { prefix: 'tagColumnSource', step: 'Columns step' },
]);

export function stepAnchorForPath(path) {
  if (!path) return null;
  const match = StepAnchorByPathPrefix.find((entry) => path.indexOf(entry.prefix) === 0);
  return match ? match.step : null;
}

/**
 * The broken rules of a 400, each anchored to the step that can fix it.
 *
 * Returns [] when the failure was not a validation one, so callers fall through to the plain
 * message path in `wizardErrors`.
 * @returns {Array<{ step: string|null, message: string }>}
 */
export function validationIssues(err) {
  const data = err && err.response && err.response.data;
  const errors = data && data.errors;
  if (!Array.isArray(errors) || !errors.length) return [];

  return errors
    .filter((entry) => entry && entry.message)
    .map((entry) => ({ step: stepAnchorForPath(entry.path), message: entry.message }));
}

/**
 * One readable line for a failure that carries no per-rule detail. Delegates to `wizardErrors`
 * so the 409 wording stays defined in exactly one place.
 */
export function describeFailure(err, fallback) {
  return describeValidationFailure(err) || describeApiError(err, fallback);
}
